import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button as MButton } from "@mui/material";
import {
  Assignment,
  Person,
  CheckCircle,
  Schedule,
  Star,
  Comment,
  Send,
  Save,
  Refresh,
} from "@mui/icons-material";
import { Checkbox, Rating, Typography } from "@material-tailwind/react";
import DateInput from "../../components/DateInput";
import useAuth from "../../hooks/useAuth";
import { appraisalState, updateMessage } from "./appraisalSlice";
import {
  createAppraisal,
  getEmployeeAppraisalList,
  getUserAssignedAppraisalDetails,
  submitFinalAppraisal,
  updateUserAdminAppraisal,
} from "../../api/appraisalApi";
import Loader from "../../components/Loader";
import { showNotification } from "../../components/Notification";
import {
  getLoginUserRoleName,
  getActionType,
  getRatingName,
  getRatingNameValue,
  getUserDataBySubmittedBy,
  ROLES,
  STATUS,
  showManagerFeedbackSection,
  allowEditMode,
  getUserDataBySubmittedRole,
  
} from "../../uitls/Utils";
import TextArea from "../../components/TextArea";
import HeaderSection from "../../components/custom/appraisal/HeaderSection";
import NavigationSection from "../../components/custom/appraisal/NavigationSection";
import CustomInput from "../../components/CustomInput";
import HeaderGroup from "../../components/custom/appraisal/HeaderGroup";

const SelfAppraisal = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedTemplate, setSelectedTemplate] = useState({});
  const [managerFeedback, setManagerFeedback] = useState({});
  const [finalReviewComment, setFinalReviewComment] = useState("");
  const [finalReviewCommentError, setFinalReviewCommentError] = useState("");

  const [rated, setRated] = useState(0);
  const [startDate, setStartDate] = useState(new Date());

  const { selectedAppraisal, employeeAppraisals, status, error, loading } =
    useSelector((state) => state.appraisalState);
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const actionType = searchParams.get("action");
  const isMangerReviewing = searchParams.get("prevPage") == "/mgr/view-appraisal";

  // Track previous selectedAppraisal ID to prevent unnecessary API calls
  const prevSelectedAppraisalIdForAPI = useRef(null);
  console.log({ selectedAppraisal, employeeAppraisals, auth })


  // Effect for fetching employee appraisals - only when selectedAppraisal ID changes

  const [allowEditRole, setAllowEditRole] = useState({ user: false, manager: false });

   const loggedInUserRole = getLoginUserRoleName(employeeAppraisals, auth)

  useEffect(() => {
    const loginUserRole = getLoginUserRoleName(employeeAppraisals, auth)
    let isAllow = false
    if (loginUserRole === "Manager") {
      isAllow = allowEditMode(selectedAppraisal?.currentAppraisalStatus, loginUserRole, isMangerReviewing)
      setAllowEditRole({ user: false, manager: isAllow, })
    } else if (loginUserRole === "User") {
      isAllow = allowEditMode(selectedAppraisal?.currentAppraisalStatus, loginUserRole)
      setAllowEditRole({ user: isAllow, manager: false })
    }

  }, [employeeAppraisals, auth])


    useEffect(() => {
      const appraisalId = searchParams.get("id");
    if (appraisalId) {
      dispatch(getUserAssignedAppraisalDetails(appraisalId));
    }
  }, [searchParams, dispatch]);



    useEffect(() => {
    if (!selectedAppraisal?.id) return;
    // Only fetch if the appraisal ID actually changed
    if (prevSelectedAppraisalIdForAPI.current === selectedAppraisal.id) {
      return;
    }
    prevSelectedAppraisalIdForAPI.current = selectedAppraisal.id;
    if (auth?.roleId == ROLES.admin || loggedInUserRole === "Manager") {
      const payload = {
        userId: auth?.user?.id,
        appraisalId:id,
        appraisalCreatedFor: selectedAppraisal?.submittedAppraisalId,
     
      };
      dispatch(getEmployeeAppraisalList(payload));
    } else {
      const payload = {
        // submittedBy:loggedInUserRole,
        userId: auth?.user?.id,
        appraisalCreatedFor:selectedAppraisal?.submittedAppraisalId || auth?.user?.id,
           appraisalId:id
          // selectedAppraisal?.submittedAppraisalId || auth?.user?.id,
      };
      dispatch(getEmployeeAppraisalList(payload));
    }
  }, [
    auth?.roleId,
    auth?.user?.id,
    dispatch,
    selectedAppraisal?.id,
    selectedAppraisal?.submittedAppraisalId,
  ]);


  


    // if (auth?.user?.id && selectedAppraisal) {
    //   const payload = {
    //     userId: auth?.user?.id,
    //     appraisalCreatedFor: selectedAppraisal?.submittedAppraisalId || auth?.u
  // Separate effect for template - only update if template ID changed
  const prevTemplateId = useRef(null);
  useEffect(() => {
    if (!selectedAppraisal?.appraisalTemplate?.id) return;

    if (prevTemplateId.current !== selectedAppraisal.appraisalTemplate?.id) {
      setSelectedTemplate(selectedAppraisal.appraisalTemplate);
      prevTemplateId.current = selectedAppraisal.appraisalTemplate?.id;
    }
  }, [selectedAppraisal?.appraisalTemplate?.id]);

  // Separate effect for final review comment
  const prevFinalReviewComment = useRef(null);
  useEffect(() => {
    if (selectedAppraisal?.currentAppraisalStatus === STATUS.FINAL_REVIEWED) {
      const newComment = selectedAppraisal?.remark || "";
      if (prevFinalReviewComment.current !== newComment) {
        setFinalReviewComment(newComment);
        prevFinalReviewComment.current = newComment;
      }
    }
  }, [selectedAppraisal?.currentAppraisalStatus, selectedAppraisal?.remark]);

  // Separate effect for rating
  const prevPerformanceRating = useRef(null);
  const prevAppraisalStatus = useRef(null);
  useEffect(() => {
    if (loggedInUserRole === "User" && selectedAppraisal?.currentAppraisalStatus === STATUS.NEED_DISCUSSION) {
      if (prevAppraisalStatus.current !== STATUS.NEED_DISCUSSION) {
        setRated(0);
        prevAppraisalStatus.current = STATUS.NEED_DISCUSSION;
        prevPerformanceRating.current = 0;
      }
    } else {
      const newRating = getRatingNameValue(selectedAppraisal?.performanceRating || 0);
      if (prevPerformanceRating.current !== newRating ||
        prevAppraisalStatus.current !== selectedAppraisal?.currentAppraisalStatus) {
        setRated(newRating);
        prevPerformanceRating.current = newRating;
        prevAppraisalStatus.current = selectedAppraisal?.currentAppraisalStatus;
      }
    }
  }, [auth?.roleId, selectedAppraisal?.currentAppraisalStatus, selectedAppraisal?.performanceRating]);

  // Separate effect for discussion date
  const prevAppraisalDiscussionDate = useRef(null);
  useEffect(() => {
    if (selectedAppraisal?.appraisalDiscussionDate) {
      const newDate = selectedAppraisal.appraisalDiscussionDate;
      if (prevAppraisalDiscussionDate.current !== newDate) {
        setStartDate(new Date(newDate));
        prevAppraisalDiscussionDate.current = newDate;
      }
    }
  }, [selectedAppraisal?.appraisalDiscussionDate]);



  // Use refs to track previous values and prevent unnecessary updates
  const prevEmployeeAppraisalsLength = useRef(employeeAppraisals?.length || 0);
  const prevSelectedAppraisalId = useRef(selectedAppraisal?.id);
  const prevSelectedTemplateId = useRef(selectedTemplate?.id);
  const prevManagerFeedbackId = useRef(managerFeedback?.id);

  useEffect(() => {
    const currentLength = employeeAppraisals?.length || 0;
    const currentAppraisalId = selectedAppraisal?.id;

    // Only update if length or appraisal ID actually changed
    if (prevEmployeeAppraisalsLength.current === currentLength &&
      prevSelectedAppraisalId.current === currentAppraisalId) {
      return;
    }

    prevEmployeeAppraisalsLength.current = currentLength;
    prevSelectedAppraisalId.current = currentAppraisalId;

    if (!employeeAppraisals || employeeAppraisals.length === 0) {
      // If no employee appraisals, initialize from selectedAppraisal template
      if (selectedAppraisal?.appraisalTemplate &&
        selectedAppraisal.appraisalTemplate?.id !== prevSelectedTemplateId.current) {
        const templateData = {
          ...selectedAppraisal.appraisalTemplate,
          additionalComments: selectedAppraisal?.additionalComments || "",
          appraisalQuestions: (selectedAppraisal.appraisalTemplate?.appraisalQuestions || []).map(q => ({
            ...q,
            textAnswer: "",
            selectedOptionId: null,
            comment: "",
          })),
        };
        setSelectedTemplate(templateData);
        prevSelectedTemplateId.current = templateData?.id;
      }
      return;
    }
    const empData = employeeAppraisals.find((e) => e.submittedBy === "User");
    const userTemplateData = empData ? prepareDataToDisplay(empData) : null;
    const loggedInUserRole = getLoginUserRoleName(employeeAppraisals, auth)
    if (currentLength === 2) {
      const managerData = employeeAppraisals.find((e) => e.submittedBy === "Manager");
      const managerTemplateData = managerData ? prepareDataToDisplay(managerData) : null;

      // Update employee template data
      if (userTemplateData && userTemplateData.appraisalQuestions && userTemplateData.appraisalQuestions.length > 0) {
        setSelectedTemplate(userTemplateData);
        prevSelectedTemplateId.current = userTemplateData?.id;
      }
      // Update manager feedback data
      if (managerTemplateData && managerTemplateData.appraisalQuestions && managerTemplateData.appraisalQuestions.length > 0) {
        setManagerFeedback(managerTemplateData);
        prevManagerFeedbackId.current = managerTemplateData?.id;
      }
    }

    else if (currentLength === 1 && loggedInUserRole === "User") {
      // Employee viewing their own appraisal
      if (userTemplateData && userTemplateData.appraisalQuestions && userTemplateData.appraisalQuestions.length > 0) {
        setSelectedTemplate(userTemplateData);
        prevSelectedTemplateId.current = userTemplateData?.id;
      }
    }
    else if (currentLength === 1 && loggedInUserRole !== "User") {
      // Manager/Admin viewing employee's submitted appraisal
      if (userTemplateData && userTemplateData.appraisalQuestions && userTemplateData.appraisalQuestions.length > 0) {
        setSelectedTemplate(userTemplateData);
        prevSelectedTemplateId.current = userTemplateData?.id;
      }
      // Initialize manager feedback from template if not already set
      if (selectedAppraisal?.appraisalTemplate?.id &&
        selectedAppraisal.appraisalTemplate?.id !== prevManagerFeedbackId.current) {
        setManagerFeedback(selectedAppraisal.appraisalTemplate);
        prevManagerFeedbackId.current = selectedAppraisal.appraisalTemplate?.id;
      }
    }
  }, [auth?.roleId, employeeAppraisals?.length, selectedAppraisal?.id]);



  useEffect(() => {
    if (status === "succeeded" && loading === false && error) {
      showNotification("success", error);
      goBackToPreviousPage();
    }
    if (status === "failed" && loading === false && error) {
      showNotification("error", error);
    }
    setTimeout(() => {
      dispatch(updateMessage());
    }, 500);
  }, [status, error, loading]);

  const goBackToPreviousPage = () => {

    setTimeout(() => {
      goBack()
    }, 800);
  };

  const prepareDataToDisplay = (data) => {
    if (!data || !data?.appraisalTemplate) {
      return {
        additionalComments: "",
        appraisalQuestions: [],
      };
    }
    let finalData = {
      ...data.appraisalTemplate,
      additionalComments: data?.additionalComments || "",
      appraisalQuestions: (
        data.appraisalTemplate?.appraisalQuestions || []
      )
        .map((question) => {
          // Convert both questionId and question.id to strings for comparison
          const questionIdStr = question.id ? String(question.id) : "";
          const filteredUserAns = data?.appraisalQuestionAnswer?.find(
            (userAnsData) => {
              const userAnsQuestionId = userAnsData?.questionId ? String(userAnsData.questionId) : "";
              return userAnsQuestionId === questionIdStr;
            }
          );
          return {
            ...question,
            comment: filteredUserAns?.comment || "",
            selectedOptionId: filteredUserAns?.selectedOptionId || null,
            textAnswer: filteredUserAns?.textAnswer || "",
          };
        })
        .sort((a, b) => {
          // Sort by group first, then by order
          const groupA = a.questionGroup || "General";
          const groupB = b.questionGroup || "General";
          if (groupA !== groupB) {
            return groupA.localeCompare(groupB);
          }
          return (a.questionOrder || 0) - (b.questionOrder || 0);
        }),
    };
    return finalData;
  };

  // Helper function to organize questions by groups
  const organizeQuestionsByGroups = (questions) => {
    const groupsMap = new Map();
    questions.forEach((q) => {
      const groupName = q.questionGroup || "General";
      if (!groupsMap.has(groupName)) {
        groupsMap.set(groupName, []);
      }
      groupsMap.get(groupName).push(q);
    });
    return Array.from(groupsMap.entries()).map(([groupName, questions]) => ({
      groupName,
      questions: questions.sort((a, b) => (a.questionOrder || 0) - (b.questionOrder || 0)),
    }));
  };

  const stateOfAppraisal = useSelector(appraisalState);

  const validateFormData = () => {
    let isValid = true;
    const allData =
      loggedInUserRole === "Manager" ? managerFeedback : selectedTemplate;

    // Ensure appraisalQuestions is an array before mapping
    if (!allData?.appraisalQuestions || !Array.isArray(allData.appraisalQuestions)) {
      return false;
    }

    const updatedItems = allData.appraisalQuestions.map((question) => {
      let data = {};

      // Check if question is required
      if (question.isRequired) {
        if (question.optionType === 0) {
          // Text input question
          if (!question?.textAnswer || question?.textAnswer?.trim() === "") {
            isValid = false;
            data = {
              ...question,
              errorMessage: "This field is required",
            };
          } else {
            data = { ...question, errorMessage: "" };
          }
        } else {
          // Choice-based question (single, multiple, checkbox)
          const selectedIds = question?.selectedOptionId || "";
          const hasSelection = selectedIds &&
            selectedIds !== "" &&
            selectedIds !== "null" &&
            selectedIds.trim() !== "";

          if (!hasSelection) {
            isValid = false;
            data = {
              ...question,
              errorMessage: "Please select an option",
            };
          } else {
            data = { ...question, errorMessage: "" };
          }
        }
      } else {
        // Not required, no validation needed - clear any existing error
        data = { ...question, errorMessage: "" };
      }

      return data;
    });

    let additionalCommentsError = "";
    // Check if user is a child manager (has managerId)


    // For main managers (not child managers), additional comments (Manager Feedback) is always required when submitting
    if (loggedInUserRole === "Manager") {
      if (!allData?.additionalComments || allData?.additionalComments?.trim()?.length === 0) {
        additionalCommentsError = "Manager feedback is required";
        isValid = false;
      }
    } else if (
      [STATUS.COMPLETED, STATUS.UNDER_REVIEW].includes(
        selectedAppraisal?.currentAppraisalStatus
      ) &&
      (!allData?.additionalComments ||
        allData?.additionalComments?.trim()?.length === 0)
    ) {
      additionalCommentsError = "Please enter additional comments";
      isValid = false;
    }

    // Validate rating for managers after NEED_DISCUSSION
    let ratingError = "";
    if (
      loggedInUserRole === "Manager" &&
      [STATUS.NEED_DISCUSSION].includes(
        selectedAppraisal?.currentAppraisalStatus
      ) &&
      rated === 0
    ) {
      ratingError = "Please select a performance rating";
      isValid = false;
    }

    const finalData = {
      ...allData,
      appraisalQuestions: updatedItems,
      additionalCommentsError: additionalCommentsError,
    };

    // Set rating error in managerFeedback if manager
    if (loggedInUserRole === "Manager") {
      setManagerFeedback({
        ...finalData,
        ratingError: ratingError,
      });
    } else {
      setSelectedTemplate(finalData);
    }
    return isValid;
  };
  // Helper function to get the appropriate data based on user role
  const getAllData = () => {
    return loggedInUserRole === "Manager" ? managerFeedback : selectedTemplate;
  };

  // Helper function to create updated items from appraisal questions
  const createUpdatedItems = (allData) => {
    if (!allData?.appraisalQuestions || !Array.isArray(allData.appraisalQuestions)) {
      return [];
    }
    return allData.appraisalQuestions.map((question) => {
      const questionId = question?.id ? String(question.id) : null;
      const answerId = getSavedQuestionAnsRowData(question?.id);

      // For text input questions (optionType === 0), use textAnswer and set selectedOptionId to null
      if (question?.optionType === 0) {
        return {
          questionId: questionId,
          id: answerId,
          selectedOptionId: null, // Always null for text questions
          textAnswer: question?.textAnswer || "", // Always include textAnswer, even if empty
          comment: question?.comment || null,
        };
      } else {
        // For choice-based questions, use selectedOptionId and set textAnswer to null
        return {
          questionId: questionId,
          id: answerId,
          selectedOptionId: question?.selectedOptionId || null,
          textAnswer: null, // Always null for choice questions
          comment: question?.comment || null,
        };
      }
    });
  };

  // Helper function to create the payload object
  const createPayload = (allData, updatedItems, action) => {
    const date = new Date(startDate);
    const isoDate = date.toISOString();
    const userData = getUserDataBySubmittedRole(employeeAppraisals, auth,"User");

    const userId = userData?.user?.id || auth.user?.id
    const templateId = allData?.id ||
      selectedAppraisal?.appraisalTemplate?.id ||
      selectedTemplate?.id ||
      managerFeedback?.id;
    if (!templateId) {
      alert("templateId is null or undefined. Cannot submit appraisal.");
      return null;
    }


    const payload = {
      appraisalQuestionAnswer: updatedItems,
      businessUnitId: allData?.businessUnit,
      userId: userId,
      additionalComments: allData?.additionalComments || "",
      templateId: templateId,
      submittedBy: getLoginUserRoleName(employeeAppraisals, auth),//User || Manager
      appraisalCreatedFor: getUserDataBySubmittedBy(employeeAppraisals, auth)?.appraisalCreatedFor || selectedAppraisal?.submittedAppraisalId || auth?.user?.id,
      actionType: getActionType(action),
      userAssignedAppraisalId: selectedAppraisal?.id,
      sendChangeRequest: managerFeedback?.sendChangeRequest ? 1 : 0,
      finalReviewComments: finalReviewComment,
      performanceRating: rated,
      appraisalDiscussionDate: isoDate,
      userAssignedAppraisalId:id
    };
    return payload;

  }


  const handleSubmit = (action) => {
    const allData = getAllData();
    const updatedItems = createUpdatedItems(allData);
    if (action === 2 && auth?.roleId === ROLES.admin) {
      if (!finalReviewComment || finalReviewComment.trim().length === 0) {
        setFinalReviewCommentError("Admin review comments are required");
        showNotification("error", "Please enter admin review comments before approving");
        return;
      } else {
        setFinalReviewCommentError("");
      }
    }

    const payload = createPayload(allData, updatedItems, action);
    // Check if payload creation failed
    if (!payload) {
      showNotification("error", "Missing required information. Please refresh the page and try again.");
      return;
    }

    if (action !== 1 && !validateFormData()) {
      return;
    }

    if (actionType === "View") {
      const updatedId = getUserDataBySubmittedBy(employeeAppraisals, auth)?.id
      const updatePayload = {
        data: payload,
        id: updatedId || id,
      };
      dispatch(updateUserAdminAppraisal(updatePayload));
      return;
    }

    if (auth.roleId === ROLES.admin && actionType === "REVIEW-FINAL-APPRAISAL") {
      dispatch(submitFinalAppraisal(payload));
      return;
    }
    dispatch(
      createAppraisal({
        editStatus: false,
        data: payload,
      })
    );
  };

  const getSavedQuestionAnsRowData = (questionId) => {
    let data = null;
    // Always try to get answerId, not just in "View" mode
    // This is needed for updates during draft/save/submit
    if (actionType === "View" || actionType === "Submit" || !actionType) {
      data = getUserDataBySubmittedBy(employeeAppraisals, auth);
      if (data?.appraisalQuestionAnswer) {
        const answer = data.appraisalQuestionAnswer.find((e) => {
          // Handle both string and UUID comparison
          const eQuestionId = e.questionId ? String(e.questionId) : "";
          const searchQuestionId = questionId ? String(questionId) : "";
          return eQuestionId === searchQuestionId;
        });
        return answer?.id || null;
      }
    }
    return null;
  };

  const updateQuestionAnswer = (qIndex, updateAnsObject) => {
    if (!selectedTemplate?.appraisalQuestions || !Array.isArray(selectedTemplate.appraisalQuestions)) {
      return;
    }
    // Ensure qIndex is within bounds
    if (qIndex < 0 || qIndex >= selectedTemplate.appraisalQuestions.length) {
      console.error(`Invalid question index: ${qIndex}. Total questions: ${selectedTemplate.appraisalQuestions.length}`);
      return;
    }
    const updatedTemplate = {
      ...selectedTemplate,
      appraisalQuestions: [
        ...selectedTemplate.appraisalQuestions.slice(0, qIndex),
        {
          ...selectedTemplate.appraisalQuestions[qIndex],
          ...updateAnsObject,
        },
        ...selectedTemplate.appraisalQuestions.slice(qIndex + 1),
      ],
    };
    setSelectedTemplate(updatedTemplate);
  };

  const updateManagerFeedback = (qIndex, updateAnsObject) => {
    if (!managerFeedback?.appraisalQuestions || !Array.isArray(managerFeedback.appraisalQuestions)) {
      return;
    }
    // Ensure qIndex is within bounds
    if (qIndex < 0 || qIndex >= managerFeedback.appraisalQuestions.length) {
      console.error(`Invalid question index: ${qIndex}. Total questions: ${managerFeedback.appraisalQuestions.length}`);
      return;
    }
    const updatedTemplate = {
      ...managerFeedback,
      appraisalQuestions: [
        ...managerFeedback.appraisalQuestions.slice(0, qIndex),
        {
          ...managerFeedback.appraisalQuestions[qIndex],
          ...updateAnsObject,
        },
        ...managerFeedback.appraisalQuestions.slice(qIndex + 1),
      ],
    };
    setManagerFeedback(updatedTemplate);
  };

  const goBack = () => {
    if (auth?.roleId === ROLES.admin) {
      navigate("/admin/assign-template");
    } else {
      navigate(-1);
    }
  }

  const checkReadOnly = (section = 1) => {
    return true


    // return (
    //   auth?.roleId === ROLES.admin ||
    //   (section === 1 && (auth?.roleId === ROLES.mgr || showUserAction())) ||
    //   (section === 2 && auth?.roleId === ROLES.emp)
    // );
  };




  const showRating = () => {

    const status = selectedAppraisal?.currentAppraisalStatus
    let readOnly = false
    let isRequired = false
    let isVisible = false

    if (auth?.roleId === ROLES.admin) {
      if ([STATUS.FINAL_REVIEWED, STATUS.APPROVED].includes(status)) {
        isVisible = true
      }

      if ([STATUS.FINAL_REVIEWED].includes(status)) {
        readOnly = true
        isRequired = false
      } else {
        readOnly = false
        isRequired = true
      }

    } else {
      if (loggedInUserRole === "User") {

        if ([STATUS.FINAL_REVIEWED].includes(status)) {
          isVisible = true
        }

        if ([STATUS.FINAL_REVIEWED].includes(status)) {
          readOnly = true
          isRequired = false
        } else if ([STATUS.NEED_DISCUSSION].includes(status)) {
          readOnly = false
          isRequired = true
        }

      } else if (loggedInUserRole === "Manager") {
        if ([STATUS.FINAL_REVIEWED, STATUS.APPROVED, STATUS.NEED_DISCUSSION].includes(status)) {
          isVisible = true
        }

        if ([STATUS.NEED_DISCUSSION].includes(status)) {
          readOnly = false
          isRequired = true
        } else if ([STATUS.FINAL_REVIEWED, STATUS.APPROVED].includes(status)) {
          readOnly = true
          isRequired = false
        }
      }
    }

    if (!isVisible || managerFeedback?.sendChangeRequest) return null

    const ratingError = managerFeedback?.ratingError || "";
    const hasRatingError = ratingError || (rated === 0 && isRequired);

    return (
      <div key="performance-rating" className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Star className="w-5 h-5 text-[#135e96]" />
          <label className="text-base font-semibold text-gray-800">
            Overall Performance Rating
            {isRequired && <span className="text-red-500 ml-1">*</span>}
          </label>
        </div>
        <div className="flex items-center gap-4">
          <Rating

            className="ml-2"
            value={rated}
            onChange={(value) => {
              setRated(value);
              // Clear error when rating is selected
              if (value > 0 && managerFeedback?.ratingError) {
                setManagerFeedback({
                  ...managerFeedback,
                  ratingError: "",
                });
              }
            }}
            readonly={readOnly}
          />
          <Typography color="blue-gray" className="font-semibold text-gray-700 text-lg">
            {getRatingName(rated)}
          </Typography>
        </div>
        {hasRatingError && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">
              {ratingError || "Please select a performance rating"}
            </p>
          </div>
        )}
      </div>
    );
  };

  const showDraftButton = () => {
    const { NOT_STARTED, COMPLETED, IN_PROGRESS, UNDER_REVIEW } = STATUS;
    const status = selectedAppraisal?.currentAppraisalStatus;
    const loggedInUserRole = getLoginUserRoleName(employeeAppraisals, auth)
    return (
      ([COMPLETED, UNDER_REVIEW].includes(status) &&
        loggedInUserRole === "Manager") ||
      ([NOT_STARTED, IN_PROGRESS].includes(status) && loggedInUserRole === "User")
    );
  };

  const showSubmittButton = () => {
    const { NEED_DISCUSSION } = STATUS;
    return [NEED_DISCUSSION].includes(
      selectedAppraisal?.currentAppraisalStatus
    );
  };

  // const makeReadOnlyForm = () => {
  //   const { NEED_DISCUSSION, UNDER_REVIEW, COMPLETED } = STATUS;

  //   return (
  //     auth.roleId === ROLES.emp ||
  //     ![UNDER_REVIEW, NEED_DISCUSSION, COMPLETED].includes(
  //       selectedAppraisal?.currentAppraisalStatus
  //     )
  //   );
  // };

  const showDiscussion = (isReadOnly) => {
    return (
      <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-md hover:shadow-lg transition-shadow duration-200 mt-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2 bg-gradient-to-br from-[#0b3a5c] to-[#135e96] rounded-lg">
            <Schedule className="w-5 h-5 text-white" />
          </div>
          <div>
            <label className="text-base font-semibold text-gray-800 block">
              Manager's availability for appraisal discussion
              {!isReadOnly && <span className="text-red-500 ml-1">*</span>}
            </label>
            {!isReadOnly && (
              <p className="text-xs text-gray-500 mt-1">
                Select a date and time for the appraisal discussion
              </p>
            )}
          </div>
        </div>
        <div className="w-full max-w-md">
          <DateInput
            id="appraisalDiscussionDate"
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            showTimeSelect={true}
            readOnly={isReadOnly}
            minDate={new Date()}
            dateFormat="MM/dd/yyyy h:mm aa"
            placeholderText="Select date and time"
            error={false}
          />
        </div>
      </div>
    );
  };
 
  console.log("loggedInUserRole::", loggedInUserRole)
  console.log("allowEditRole::", allowEditRole)

  const isVisiblManagerFeeback = showManagerFeedbackSection(selectedAppraisal?.currentAppraisalStatus, loggedInUserRole, isMangerReviewing)
  console.log("isVisiblManagerFeeback::", isVisiblManagerFeeback)

  const allowOnlyAdminToEdit = auth.roleId === ROLES.admin && [STATUS.APPROVED].includes(
    selectedAppraisal?.currentAppraisalStatus
  )
  return (
    <>
      {stateOfAppraisal?.loading && <Loader />}
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-gray-50 to-blue-100 p-4 md:p-6">
        {/* Navigation Section */}

        <NavigationSection
          title={selectedAppraisal?.currentAppraisalStatus || "Appraisal Review"}
          onBack={goBack}

        />
        {/* Main Content - Responsive width */}
        <div className="w-full">
          <div className={`grid grid-cols-1 ${!isVisiblManagerFeeback ? '' : 'xl:grid-cols-2'} gap-6`}>
            {/* Employee Appraisal Section */}
            <div className={`bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden ${!isVisiblManagerFeeback ? 'xl:w-[75%] xl:mx-auto' : ''}`}>
              {selectedTemplate?.templateTitle && (
                <div>
                  {/* Section Header */}
                  <HeaderSection
                    title={"Employee Appraisal Review"}
                    icon={<Person className="text-white" />}
                  />

                  {/* Questions Section */}
                  <div className="p-6 space-y-8">
                    {(() => {
                      const questionGroups = organizeQuestionsByGroups(selectedTemplate.appraisalQuestions || []);
                      let globalQuestionIndex = 0;

                      return questionGroups.map((group, groupIndex) => (
                        <div key={`group-${groupIndex}`} className="space-y-4">
                          {/* Group Header - Always show group name */}
                          <HeaderGroup group={group} />

                          {/* Questions in Group */}
                          <div className="space-y-6">
                            {group.questions.map((question, localIndex) => {
                              // Find the actual index in the flattened array
                              const actualIndex = selectedTemplate.appraisalQuestions.findIndex(q => q.id === question.id);
                              // Fallback to global index if question not found (shouldn't happen)
                              const qIndex = actualIndex >= 0 ? actualIndex : globalQuestionIndex++;
                              return (
                                <div
                                  key={"employee" + qIndex}
                                  className="bg-gray-50 rounded-xl p-6 border border-[#ddd] shadow-sm hover:shadow-md transition-shadow duration-200"
                                >
                                  {/* Question Header */}
                                  <div className="mb-4">
                                    <div className="flex items-center gap-2 mb-2">
                                      <h3 className="text-lg font-semibold text-gray-800">
                                        {`${qIndex + 1}. ${question.questionTitle}`}
                                      </h3>
                                      {question.isRequired && (
                                        <span className="text-red-500 text-lg font-bold">*</span>
                                      )}
                                    </div>
                                    {question.questionSubTitle && (
                                      <p className="text-gray-600 text-sm leading-relaxed">
                                        {question.questionSubTitle}
                                      </p>
                                    )}
                                  </div>

                                  {/* Question Type: Text Input */}
                                  {question.optionType === 0 && (
                                    <div className="mb-4">
                                      <textarea
                                        readOnly={!allowEditRole.user}
                                        className={`w-full p-3 text-sm text-gray-900 bg-white rounded-lg border transition-all duration-200 resize-none font-medium ${checkReadOnly(1)
                                          ? "border-gray-200 bg-gray-50"
                                          : question?.errorMessage
                                            ? "border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                            : "border-gray-200 focus:ring-2 focus:ring-[#135e96] focus:border-[#135e96]"
                                          }`}
                                        placeholder="Enter your answer here..."
                                        rows={4}
                                        value={question?.textAnswer || ""}
                                        onChange={(e) => {
                                          if (!allowEditRole.user) return;
                                          const jsonAns = {
                                            textAnswer: e.target.value,
                                            errorMessage: "",
                                          };
                                          updateQuestionAnswer(qIndex, jsonAns);
                                        }}
                                      />
                                    </div>
                                  )}

                                  {/* Question Types: Single Choice, Multiple Choice, Checkbox */}
                                  {question.optionType !== 0 && (
                                    <div className="space-y-3 mb-4">
                                      {question.questionOptions?.map(
                                        (optionObj, optionIndex) => {
                                          const optionValue = optionObj?.["optionTitle"];
                                          // Handle multiple selections for checkbox (type 3) and multiple choice (type 2)
                                          const isSelected = question.optionType === 2 || question.optionType === 3
                                            ? (question.selectedOptionId || "").split(",").includes(optionObj?.id?.toString())
                                            : question.selectedOptionId === optionObj?.id;

                                          return (
                                            <div
                                              key={"employeeInternal" + optionIndex}
                                              className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-100 hover:border-blue-200 transition-colors duration-200 shadow-sm"
                                            >
                                              <span className="font-semibold text-blue-600 min-w-[20px]">
                                                {String.fromCharCode(65 + optionIndex)}.
                                              </span>
                                              {question.optionType === 3 ? (
                                                // Checkbox - multiple selections
                                                <CustomInput
                                                  readOnly={!allowEditRole.user}
                                                  type="checkbox"
                                                  name={`employee-checkbox-${qIndex}-${optionIndex}`}
                                                  checked={isSelected}
                                                  onChange={(e) => {
                                                    if (!allowEditRole.user) return;
                                                    const currentSelections = (question.selectedOptionId || "").split(",").filter(id => id);
                                                    let newSelections;
                                                    if (e.target.checked) {
                                                      newSelections = [...currentSelections, optionObj?.id?.toString()];
                                                    } else {
                                                      newSelections = currentSelections.filter(id => id !== optionObj?.id?.toString());
                                                    }
                                                    const jsonAns = {
                                                      selectedOptionId: newSelections.join(","),
                                                      errorMessage: "",
                                                    };
                                                    updateQuestionAnswer(qIndex, jsonAns);
                                                  }}
                                                />
                                              ) : question.optionType === 2 ? (
                                                // Multiple Choice - radio but can select multiple (using comma-separated)
                                                <CustomInput
                                                  readOnly={!allowEditRole.user}
                                                  type="checkbox"
                                                  name={`employee-multiple-${qIndex}-${optionIndex}`}
                                                  checked={isSelected}
                                                  onChange={(e) => {
                                                    if (!allowEditRole.user) return;
                                                    const currentSelections = (question.selectedOptionId || "").split(",").filter(id => id);
                                                    let newSelections;
                                                    if (e.target.checked) {
                                                      newSelections = [...currentSelections, optionObj?.id?.toString()];
                                                    } else {
                                                      newSelections = currentSelections.filter(id => id !== optionObj?.id?.toString());
                                                    }
                                                    const jsonAns = {
                                                      selectedOptionId: newSelections.join(","),
                                                      errorMessage: "",
                                                    };
                                                    updateQuestionAnswer(qIndex, jsonAns);
                                                  }}
                                                />
                                              ) : (
                                                // Single Choice - radio button
                                                <CustomInput
                                                  readOnly={!allowEditRole.user}
                                                  type="radio"
                                                  name={`employee${question.id || qIndex}`}
                                                  checked={isSelected}
                                                  onChange={(e) => {
                                                    if (!allowEditRole.user) return;
                                                    const jsonAns = {
                                                      selectedOptionId: optionObj?.id,
                                                      errorMessage: "",
                                                    };
                                                    updateQuestionAnswer(qIndex, jsonAns);
                                                  }}
                                                />
                                              )}
                                              <span className="text-gray-700 flex-1">
                                                {optionValue}
                                              </span>
                                            </div>
                                          );
                                        }
                                      )}
                                    </div>
                                  )}

                                  {/* Error Message */}
                                  {question?.errorMessage && (
                                    <div className="mb-3 p-2 bg-red-50 border border-red-100 rounded-lg">
                                      <p className="text-red-600 text-sm">
                                        {question?.errorMessage}
                                      </p>
                                    </div>
                                  )}

                                  {question.optionType !== 0 &&
                                    <TextArea
                                      readOnly={!allowEditRole.user}
                                      labelIcon={<Comment className="w-5 h-5 text-[#135e96]" />}
                                      labelText={"Comments"}
                                      numOfRows={4}
                                      placeholder="Write your comments here..."
                                      value={question?.comment || ""}
                                      onChange={(e) => {
                                        const jsonAns = { comment: e.target.value };
                                        updateQuestionAnswer(qIndex, jsonAns);
                                      }}
                                    />
                                  }

                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ));
                    })()}


                    <TextArea
                      readOnly={!allowEditRole.user}
                      labelIcon={<Comment className="w-5 h-5 text-[#135e96]" />}
                      labelText={"Additional Comments"}
                      value={selectedTemplate?.additionalComments || ""}
                      onChange={(e) => {
                        setSelectedTemplate({
                          ...selectedTemplate,
                          additionalComments: e.target.value,
                        });
                      }}
                    />



                  </div>

                  {/* Action Buttons */}
                  {allowEditRole.user && (
                    <div className="mt-4 pt-4 pb-4 mb-4 border-gray-100">
                      <div className="flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center w-full">
                        {showDraftButton() && (
                          <MButton
                            loading={stateOfAppraisal?.loading}
                            variant="outlined"
                            startIcon={<Save />}
                            onClick={() => handleSubmit(1)}
                            className="w-full sm:w-auto min-w-[150px] px-6 py-3 border-2 border-[#135e96] text-[#135e96] hover:bg-[#135e96] hover:text-white transition-all duration-200 font-semibold rounded-lg shadow-sm hover:shadow-md"
                          >
                            Save Draft
                          </MButton>
                        )}
                        <MButton
                          loading={stateOfAppraisal?.loading}
                          variant="contained"
                          startIcon={<Send />}
                          onClick={() => handleSubmit(2)}
                          className="w-full sm:w-auto min-w-[150px] px-6 py-3 m-b-4 bg-gradient-to-r from-[#0b3a5c] to-[#135e96] hover:from-[#135e96] hover:to-[#0b3a5c] transition-all duration-200 font-semibold rounded-lg shadow-md hover:shadow-lg text-white"
                        >
                          Submit
                        </MButton>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Manager Feedback Section */}
            {(() => {
              if (!isVisiblManagerFeeback) return null;
console.log("managerFeedback::",managerFeedback)
              return (
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                  {(managerFeedback?.templateTitle ||
                    (
                      employeeAppraisals?.length >= 1)) && (
                      <div>
                        <HeaderSection
                          title={"Manager Appraisal Review"}
                          icon={<Assignment className="text-white" />}
                        />

                        <div className="p-6 space-y-8">
                          {managerFeedback?.appraisalQuestions?.length > 0 ? (
                            <>
                              {(() => {
                                
                                const questionGroups = organizeQuestionsByGroups(managerFeedback.appraisalQuestions || []);
                                let globalQuestionIndex = 0;
                                const lStatus=selectedAppraisal?.currentAppraisalStatus
if(loggedInUserRole=="User" && [STATUS.UNDER_REVIEW,STATUS.CHANGE_REQUEST,STATUS.NEED_DISCUSSION,STATUS.COMPLETED].includes(lStatus) ||employeeAppraisals.length<=1 ) return

               
                                return questionGroups.map((group, groupIndex) => (
                                  <div key={`manager-group-${groupIndex}`} className="space-y-4">
                                    {/* Group Header - Always show group name */}
                                    <HeaderGroup group={group} />

                                    {/* Questions in Group */}
                                    <div className="space-y-6">
                                      {group.questions.map((question, localIndex) => {
                                        // Find the actual index in the flattened array
                                        const actualIndex = managerFeedback.appraisalQuestions.findIndex(q => q.id === question.id);
                                        // Fallback to global index if question not found (shouldn't happen)
                                        const qIndex = actualIndex >= 0 ? actualIndex : globalQuestionIndex++;
                                        return (
                                          <div
                                            key={"managerFeedback" + qIndex}
                                            className="bg-gray-50 rounded-xl p-6 border border-[#ddd] shadow-sm hover:shadow-md transition-shadow duration-200"
                                          >
                                            <div className="mb-4">
                                              <div className="flex items-center gap-2 mb-2">
                                                <h3 className="text-lg font-semibold text-gray-800">
                                                  {`${qIndex + 1}. ${question.questionTitle}`}
                                                </h3>
                                                {question.isRequired && (
                                                  <span className="text-red-500 text-lg font-bold">*</span>
                                                )}
                                              </div>
                                              {question.questionSubTitle && (
                                                <p className="text-gray-600 text-sm leading-relaxed">
                                                  {question.questionSubTitle}
                                                </p>
                                              )}
                                            </div>

                                            {/* Question Type: Text Input */}
                                            {question.optionType === 0 && (
                                              <div className="mb-4">
                                                <textarea
                                                  readOnly={!allowEditRole.manager}
                                                  className={`w-full p-3 text-sm text-gray-900 bg-white rounded-lg border transition-all duration-200 resize-none font-medium ${!allowEditRole.manager
                                                    ? "border-gray-200 bg-gray-50"
                                                    : question?.errorMessage
                                                      ? "border-red-300 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                                      : "border-gray-200 focus:ring-2 focus:ring-[#135e96] focus:border-[#135e96]"
                                                    }`}
                                                  placeholder="Enter your answer here..."
                                                  rows={4}
                                                  value={question?.textAnswer || ""}
                                                  onChange={(e) => {
                                                    if (!allowEditRole.manager) return;
                                                    const jsonAns = {
                                                      textAnswer: e.target.value,
                                                      errorMessage: "",
                                                    };
                                                    updateManagerFeedback(qIndex, jsonAns);
                                                  }}
                                                />
                                              </div>
                                            )}

                                            {/* Question Types: Single Choice, Multiple Choice, Checkbox */}
                                            {question.optionType !== 0 && (
                                              <div className="space-y-3 mb-4">
                                                {question.questionOptions?.map(
                                                  (optionObj, optionIndex) => {
                                                    const optionValue =
                                                      optionObj?.["optionTitle"];
                                                    // Handle multiple selections for checkbox (type 3) and multiple choice (type 2)
                                                    const isSelected = question.optionType === 2 || question.optionType === 3
                                                      ? (question.selectedOptionId || "").split(",").includes(optionObj?.id?.toString())
                                                      : question.selectedOptionId === optionObj?.id;

                                                    return (
                                                      <div
                                                        key={
                                                          "managerFeedback-internal" +
                                                          optionIndex
                                                        }
                                                        className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-100 hover:border-blue-200 transition-colors duration-200 shadow-sm"
                                                      >
                                                        <span className="font-semibold text-blue-600 min-w-[20px]">
                                                          {String.fromCharCode(
                                                            65 + optionIndex
                                                          )}
                                                          .
                                                        </span>
                                                        {question.optionType === 3 ? (
                                                          // Checkbox - multiple selections
                                                          <CustomInput
                                                            readOnly={!allowEditRole.manager}
                                                            type="checkbox"
                                                            name={`manager-checkbox-${qIndex}-${optionIndex}`}
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                              if (!allowEditRole.manager) return;
                                                              const currentSelections = (question.selectedOptionId || "").split(",").filter(id => id);
                                                              let newSelections;
                                                              if (e.target.checked) {
                                                                newSelections = [...currentSelections, optionObj?.id?.toString()];
                                                              } else {
                                                                newSelections = currentSelections.filter(id => id !== optionObj?.id?.toString());
                                                              }
                                                              const jsonAns = {
                                                                selectedOptionId: newSelections.join(","),
                                                                errorMessage: "",
                                                              };
                                                              updateManagerFeedback(qIndex, jsonAns);
                                                            }}
                                                          />
                                                        ) : question.optionType === 2 ? (
                                                          // Multiple Choice - checkbox but can select multiple
                                                          <CustomInput
                                                            readOnly={!allowEditRole.manager}
                                                            type="checkbox"
                                                            name={`manager-multiple-${qIndex}-${optionIndex}`}
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                              if (!allowEditRole.manager) return;
                                                              const currentSelections = (question.selectedOptionId || "").split(",").filter(id => id);
                                                              let newSelections;
                                                              if (e.target.checked) {
                                                                newSelections = [...currentSelections, optionObj?.id?.toString()];
                                                              } else {
                                                                newSelections = currentSelections.filter(id => id !== optionObj?.id?.toString());
                                                              }
                                                              const jsonAns = {
                                                                selectedOptionId: newSelections.join(","),
                                                                errorMessage: "",
                                                              };
                                                              updateManagerFeedback(qIndex, jsonAns);
                                                            }}
                                                          />
                                                        ) : (
                                                          // Single Choice - radio button
                                                          <CustomInput
                                                            readOnly={!allowEditRole.manager}
                                                            type="radio"
                                                            name={`manager${question.id || qIndex}`}
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                              if (!allowEditRole.manager) return;
                                                              const jsonAns = {
                                                                selectedOptionId:
                                                                  optionObj?.id,
                                                                errorMessage: "",
                                                              };
                                                              updateManagerFeedback(
                                                                qIndex,
                                                                jsonAns
                                                              );
                                                            }}
                                                          />
                                                        )}
                                                        <span className="text-gray-700 flex-1">
                                                          {optionValue}
                                                        </span>
                                                      </div>
                                                    );
                                                  }
                                                )}
                                              </div>
                                            )}
                                            {question?.errorMessage && (
                                              <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                                                <p className="text-red-600 text-sm">
                                                  {question?.errorMessage}
                                                </p>
                                              </div>
                                            )}

                                            {question.optionType !== 0 &&
                                              <TextArea
                                                readOnly={!allowEditRole.manager}
                                                labelIcon={<Comment className="w-5 h-5 text-[#135e96]" />}
                                                labelText={"Comments"}
                                                numOfRows={4}
                                                placeholder="Write your comments here..."
                                                value={question?.comment || ""}
                                                onChange={(e) => {
                                                  const jsonAns = { comment: e.target.value };
                                                  updateManagerFeedback(qIndex, jsonAns);
                                                }}
                                              />
                                            }
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ));
                              })()}

                              <TextArea
                                readOnly={!allowEditRole.manager}
                                labelIcon={<Comment className="w-5 h-5 text-[#135e96]" />}
                                labelText={"Manager Feedback"}
                                value={managerFeedback?.additionalComments || ""}
                                error={managerFeedback?.additionalCommentsError}
                                isRequired={true}
                                onChange={(e) => {
                                  setManagerFeedback({
                                    ...managerFeedback,
                                    additionalComments: e.target.value,
                                    additionalCommentsError: "",
                                  });
                                }}
                              />




                              <div className="mt-6 space-y-6">
                                {loggedInUserRole === "Manager" &&
                                  ![
                                    STATUS.NEED_DISCUSSION,
                                    STATUS.FINAL_REVIEWED,
                                    STATUS.APPROVED,
                                    STATUS.CHANGE_REQUEST
                                  ].includes(selectedAppraisal?.currentAppraisalStatus) && (
                                    <div className="bg-white p-5 rounded-xl border border-[#ddd] shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.12)] transition-shadow duration-200">
                                      <Checkbox
                                        className="rounded-sm"
                                        color="blue"
                                        checked={managerFeedback?.sendChangeRequest || false}
                                        onClick={() =>
                                          setManagerFeedback({
                                            ...managerFeedback,
                                            sendChangeRequest:
                                              !managerFeedback?.sendChangeRequest,
                                          })
                                        }
                                        label={
                                          <span className="text-gray-700 font-medium">
                                            Do you want to send change request to employee?
                                          </span>
                                        }
                                      />
                                    </div>
                                  )}



                                {/* Manager availability section - above buttons, hidden when change request is selected */}
                                {!managerFeedback?.sendChangeRequest && (
                                  <>
                                    {["NEED_DISCUSSION"].includes(
                                      selectedAppraisal?.currentAppraisalStatus
                                    ) &&
                                      loggedInUserRole !== "Manager" &&
                                      showDiscussion(true)}

                                    {[
                                      "IN_PROGRESS",
                                      "COMPLETED",
                                      "UNDER_REVIEW",
                                      "NEED_DISCUSSION",
                                    ].includes(selectedAppraisal?.currentAppraisalStatus) &&
                                      loggedInUserRole === "Manager" &&
                                      showDiscussion(selectedAppraisal?.currentAppraisalStatus == STATUS.NEED_DISCUSSION ? true : false)}
                                  </>
                                )}
                              </div>

                              {showRating()}

                              {loggedInUserRole === "Manager" &&
                                <div className="flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center w-full pt-6">
                                  {!managerFeedback?.sendChangeRequest &&
                                    showDraftButton() && (
                                      <MButton
                                        disabled={stateOfAppraisal?.loading}
                                        variant="outlined"
                                        startIcon={<Save />}
                                        onClick={() => handleSubmit(1)}
                                        className="w-full sm:w-auto min-w-[150px] px-6 py-3 border-2 border-[#135e96] text-[#135e96] hover:bg-[#135e96] hover:text-white transition-all duration-200 font-semibold rounded-lg shadow-sm hover:shadow-md"
                                      >
                                        Save Draft
                                      </MButton>
                                    )}
                                  {!managerFeedback?.sendChangeRequest &&
                                    showDraftButton() && (
                                      <MButton
                                        disabled={stateOfAppraisal?.loading}
                                        variant="contained"
                                        startIcon={<Schedule />}
                                        onClick={() => handleSubmit(3)}
                                        className="w-full sm:w-auto min-w-[150px] px-6 py-3 bg-gradient-to-r from-[#0b3a5c] to-[#135e96] hover:from-[#135e96] hover:to-[#0b3a5c] transition-all duration-200 font-semibold rounded-lg shadow-md hover:shadow-lg text-white"
                                      >
                                        Appraisal Discussion
                                      </MButton>
                                    )}
                                  {showSubmittButton() && (
                                    <MButton
                                      disabled={stateOfAppraisal?.loading}
                                      variant="contained"
                                      startIcon={<Send />}
                                      onClick={() => handleSubmit(2)}
                                      className="w-full sm:w-auto min-w-[150px] px-6 py-3 bg-gradient-to-r from-[#0b3a5c] to-[#135e96] hover:from-[#135e96] hover:to-[#0b3a5c] transition-all duration-200 font-semibold rounded-lg shadow-md hover:shadow-lg text-white"
                                    >
                                      Submit
                                    </MButton>
                                  )}
                                  {managerFeedback?.sendChangeRequest && (
                                    <MButton
                                      disabled={stateOfAppraisal?.loading}
                                      variant="contained"
                                      startIcon={<Refresh />}
                                      onClick={() => handleSubmit(4)}
                                      className="w-full sm:w-auto min-w-[150px] px-6 py-3 bg-gradient-to-r from-[#0b3a5c] to-[#135e96] hover:from-[#135e96] hover:to-[#0b3a5c] transition-all duration-200 font-semibold rounded-lg shadow-md hover:shadow-lg text-white"
                                    >
                                      Change Request
                                    </MButton>
                                  )}
                                </div>
                              }


                            </>
                          ) : (
                            <div className="text-center py-12">
                              <Assignment className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                              <h3 className="text-lg font-semibold text-gray-600 mb-2">
                                Manager Feedback Not Available
                              </h3>
                              <p className="text-gray-500">
                                Manager feedback will appear here once the manager
                                completes their review.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                  {loggedInUserRole === "User" &&
                    ["APPROVED"].includes(
                      selectedAppraisal?.currentAppraisalStatus
                    ) && (
                      <div className="bg-green-50 border-t border-green-200 px-6 py-4">
                        <div className="flex items-center gap-2 text-green-700">
                          <CheckCircle className="w-5 h-5" />
                          <p className="font-medium">Status: Pending Final Review by HR/Admin</p>
                        </div>
                      </div>
                    )}
                </div>
              );
            })()}
          </div>

          {(finalReviewComment || allowOnlyAdminToEdit) &&
            [STATUS.APPROVED, STATUS.FINAL_REVIEWED].includes(
              selectedAppraisal?.currentAppraisalStatus
            ) && (
              <div className="mt-6">
                <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.08)] border border-gray-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-[#0b3a5c] to-[#135e96] px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Star className="text-white" />
                      <h2 className="text-xl font-bold text-white">
                        Final Review Comments
                      </h2>
                    </div>
                  </div>

                  <div >

                    <TextArea
                      className="border-0 !"
                      readOnly={!allowOnlyAdminToEdit}
                      labelIcon={<Comment className="w-5 h-5 text-[#135e96]" />}
                      labelText={"Admin Review Comments"}
                      value={finalReviewComment || ""}
                      error={finalReviewCommentError}
                      isRequired={true}
                      onChange={(e) => {
                        setFinalReviewComment(e.target.value);
                        if (finalReviewCommentError && e.target.value.trim().length > 0) {
                          setFinalReviewCommentError("");
                        }
                      }}
                    />
                  </div>

                  {allowOnlyAdminToEdit &&
                    <div className="mt-6 flex justify-center">
                      <MButton
                        disabled={stateOfAppraisal?.loading}
                        variant="contained"
                        startIcon={<CheckCircle />}
                        onClick={() => handleSubmit(2)}
                        className="px-8 py-3 bg-gradient-to-r from-[#0b3a5c] to-[#135e96] hover:from-[#135e96] hover:to-[#0b3a5c] transition-all duration-200 text-white font-semibold shadow-lg hover:shadow-xl"
                      >
                        Approve
                      </MButton>
                    </div>
                  }

                </div>
              </div>
            )}
        </div>
      </div>
    </>
  );
};
export default SelfAppraisal;
